import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, Home, Calendar, Users, MapPin } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [booking, setBooking] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let cancelled = false;
    let timerId;
    pollCount.current = 0;

    const pollPaymentStatus = async () => {
      if (cancelled) return;
      if (pollCount.current >= 10) {
        setStatus("timeout");
        return;
      }

      try {
        const res = await axios.get(`${API}/bookings/status/${sessionId}`);
        if (cancelled) return;
        const data = res.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setBooking(data.booking);
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        if (cancelled) return;
      }

      pollCount.current += 1;
      timerId = window.setTimeout(pollPaymentStatus, 2000);
    };

    pollPaymentStatus();
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [sessionId]);

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24 flex items-center justify-center min-h-[80vh]">
        <div className="max-w-lg w-full">
          {status === "loading" && (
            <div className="text-center animate-fade-in-up">
              <div className="w-20 h-20 rounded-full bg-[#F5F5F7] flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 animate-spin text-[#86868B]" />
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Zahlung wird überprüft
              </h1>
              <p className="text-[#86868B]">Bitte warten Sie einen Moment...</p>
            </div>
          )}

          {status === "success" && booking && (
            <div className="text-center animate-fade-in-up">
              <div className="w-20 h-20 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#34C759]" />
              </div>
              <h1
                data-testid="booking-success-title"
                className="text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Buchung bestätigt!
              </h1>
              <p className="text-[#86868B] mb-8">
                Vielen Dank, {booking.guest_name}. Ihre Buchung wurde erfolgreich abgeschlossen.
              </p>

              {/* Booking Details Card */}
              <div data-testid="booking-details-card" className="bg-white rounded-3xl border border-black/5 p-6 text-left shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <h3 className="font-semibold text-[#1D1D1F] text-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {booking.listing_name}
                </h3>

                {booking.hostaway_reservation_id && (
                  <div data-testid="hostaway-reservation-badge" className="mb-4 bg-[#34C759]/10 rounded-xl px-4 py-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    <span className="text-sm font-medium text-[#1D1D1F]">
                      Reservierung #{booking.hostaway_reservation_id} bei Hostaway bestätigt
                    </span>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-[#86868B]" />
                    <span className="text-[#1D1D1F]">
                      {booking.checkin} bis {booking.checkout} ({booking.nights} {booking.nights === 1 ? "Nacht" : "Nächte"})
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Users className="w-4 h-4 text-[#86868B]" />
                    <span className="text-[#1D1D1F]">{booking.guests} {booking.guests === 1 ? "Gast" : "Gäste"}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-black/5">
                  <div className="flex justify-between font-semibold">
                    <span className="text-[#1D1D1F]">Bezahlt</span>
                    <span className="text-[#1D1D1F]">{booking.total?.toFixed(2)} {booking.currency}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button
                  data-testid="go-home-button"
                  onClick={() => navigate("/")}
                  className="flex-1 bg-black text-white rounded-full py-5 font-medium btn-press"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Zur Startseite
                </Button>
                <Button
                  data-testid="browse-more-button"
                  onClick={() => navigate("/unterkuenfte")}
                  variant="outline"
                  className="flex-1 rounded-full py-5 font-medium border-black/10"
                >
                  Mehr Unterkünfte
                </Button>
              </div>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div className="text-center animate-fade-in-up">
              <div className="w-20 h-20 rounded-full bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-[#FF3B30]" />
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {status === "expired" ? "Sitzung abgelaufen" : status === "timeout" ? "Zeitüberschreitung" : "Fehler"}
              </h1>
              <p className="text-[#86868B] mb-8">
                {status === "expired"
                  ? "Ihre Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut."
                  : status === "timeout"
                  ? "Die Statusprüfung hat zu lange gedauert. Bitte überprüfen Sie Ihre E-Mail."
                  : "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."}
              </p>
              <Button
                data-testid="try-again-button"
                onClick={() => navigate("/unterkuenfte")}
                className="bg-black text-white rounded-full px-8 py-5 font-medium btn-press"
              >
                Zurück zu Unterkünften
              </Button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
